/**
 * Touch Gesture Utilities
 * 
 * Pure functions for calculating touch gesture metrics and determining
 * when to close a drawer based on swipe velocity and distance.
 */

/**
 * Touch position and timing data
 */
export interface TouchData {
  y: number;
  time: number;
}

/**
 * Calculate velocity of a swipe gesture
 * 
 * @param deltaY - Vertical distance traveled
 * @param deltaTime - Time elapsed in milliseconds
 * @returns Velocity in pixels per millisecond
 */
export function calculateVelocity(deltaY: number, deltaTime: number): number {
  if (deltaTime === 0) return 0;
  return Math.abs(deltaY) / deltaTime;
}

/**
 * Determine if a swipe gesture should close the drawer
 * 
 * Closes if:
 * 1. Swipe down more than 100px, OR
 * 2. Swipe down more than 50px with sufficient velocity (>0.3)
 * 
 * @param deltaY - Vertical distance traveled (positive = down)
 * @param velocity - Swipe velocity in pixels per millisecond
 * @returns Whether the drawer should close
 */
export function shouldCloseOnSwipe(deltaY: number, velocity: number): boolean {
  return deltaY > 100 || (deltaY > 50 && velocity > 0.3);
}

/**
 * Calculate the transform offset for visual feedback during swipe
 * 
 * @param currentY - Current touch Y position
 * @param startY - Starting touch Y position
 * @returns Transform offset in pixels (clamped to >= 0)
 */
export function calculateSwipeOffset(currentY: number, startY: number): number {
  return Math.max(0, currentY - startY);
}
